import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, ApisLogging } from 'lib-trend-core';

export interface ApiLoggingViewData {
  item?: ApisLogging;
  body?: object;
  title?: string;
}

@Component({
    selector: 'app-report-api-loggings-view',
    templateUrl: './report-api-loggings-view.component.html',
    styleUrl: './report-api-loggings-view.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportApiLoggingsViewComponent extends AbstractComponent {

  readonly HIDDEN_HEADERS: string[] = [];

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: ApiLoggingViewData,
    private dialogRef: MatDialogRef<ReportApiLoggingsViewComponent>,
  ) {
    super(injector);
  }

  get item(): ApisLogging | undefined {
    return this.data?.item;
  }

  get isBodyOnly(): boolean {
    return !this.data?.item && !!this.data?.body;
  }

  get dialogTitle(): string {
    return this.data?.title ?? this.t('report.apiLoggingsView.requestDetails');
  }

  getStatusBadgeClass(): string {
    const code = this.item?.statusCode;
    if (!code) return 'bg-secondary';
    if (code >= 200 && code < 300) return 'bg-success';
    if (code >= 300 && code < 400) return 'bg-info';
    if (code >= 400 && code < 500) return 'bg-warning';
    if (code >= 500) return 'bg-danger';
    return 'bg-secondary';
  }

  getStatusLabel(): string {
    const code = this.item?.statusCode;
    if (!code) return '-';
    if (code >= 200 && code < 300) return this.t('report.apiLoggingsView.statusSuccess');
    if (code >= 300 && code < 400) return this.t('report.apiLoggingsView.statusRedirect');
    if (code >= 400 && code < 500) return this.t('report.apiLoggingsView.statusClientError');
    if (code >= 500) return this.t('report.apiLoggingsView.statusServerError');
    return '-';
  }

  getMethodBadgeClass(): string {
    const method = this.item?.method;
    switch (method) {
      case 'GET': return 'bg-primary';
      case 'POST': return 'bg-success';
      case 'PUT': return 'bg-warning';
      case 'PATCH': return 'bg-info';
      case 'DELETE': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getPerformanceBadgeClass(): string {
    const ms = this.item?.durationMs;
    if (ms === undefined || ms === null) return 'bg-secondary';
    if (ms < 300) return 'bg-success';
    if (ms < 1000) return 'bg-warning';
    return 'bg-danger';
  }

  getPerformanceLabel(): string {
    const ms = this.item?.durationMs;
    if (ms === undefined || ms === null) return this.t('report.apiLoggingsView.notAvailable');
    if (ms < 300) return this.t('report.apiLoggingsView.performanceFast');
    if (ms < 1000) return this.t('report.apiLoggingsView.performanceModerate');
    return this.t('report.apiLoggingsView.performanceSlow');
  }

  getBrowser(): string {
    const headers = this.item?.headers as Record<string, string> | undefined;
    const raw = headers?.['sec-ch-ua'] ?? headers?.['Sec-Ch-Ua'] ?? '';
    if (!raw) return '';
    const SKIP = ['not.a/brand', 'not a brand', 'chromium'];
    for (const part of raw.split(',')) {
      const m = part.trim().match(/^"([^"]+)";\s*v="([^"]+)"$/);
      if (m && !SKIP.some(s => m[1].toLowerCase().includes(s))) {
        return `${m[1]} ${m[2]}`;
      }
    }
    return '';
  }

  getPlatform(): string {
    const headers = this.item?.headers as Record<string, string> | undefined;
    const raw = headers?.['sec-ch-ua-platform'] ?? headers?.['Sec-Ch-Ua-Platform'] ?? '';
    return raw.replace(/^"|"$/g, '').trim();
  }

  formatJson(obj: any): string {
    if (!obj) return '';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
